import { RootState } from '@Redux/store'
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'

import { ConfigMarket, IConfigMarket, IMarketInfo, IPublicServiceConnection } from '@App/redux/interfaces'
import PublicServiceConnection from '@App/sockets/public_service_connection_new'
import SocketMiddleware from '@App/sockets/socket_middleware'
import { Events, ISubscribeParams, SocketState } from '@App/sockets/types'

import { getMarket, setKlines } from '@Redux/slices/config/ConfigSlice'

import {
  IActualOrderBook,
  IActualRate,
  IActualTradesHistory,
  ICurrenciesSourceTarget,
  ITickerData,
  ITickerEntity,
  ITickerEntityData,
} from '@Interfaces/CommonInterfaces'
import { TradesEntity } from '@Interfaces/Entities'
import localStorageManager from '@App/cookies/local_storage_manager'
const initialState: IPublicServiceConnection = {
  rates: {},
  rates_ticker: {
    entities: {},
    period: '',
  },
  order_book_by_market: {},
  trades_history_by_market: {},
  markets: [],
  klinesById: {},
  socket_status: SocketState.Closed,
  last_selected_tab: '',
  next_cursor: null,
  has_more: true,
  is_initial_load: true,
}
export const setConfigPublicServiceConnection = createAsyncThunk('socket/setConfig', async (path: string) => {
  PublicServiceConnection.setApiUrl(path)
})
let processMessageFunc: any
const getProcessMessageFunc = (dispatch: any): (() => {}) => {
  if (processMessageFunc) {
    return processMessageFunc
  }
  processMessageFunc = (message: any) => {
    switch (message.event) {
      case Events.GetRate:
        const rate = message.payload.rate
        const rate_currency = message.payload.source_currency
        const rate_target_currency = message.payload.target_currency
        dispatch(
          setRateAsync({
            key: rate_currency + '_' + rate_target_currency,
            rate,
            time: message.payload.time,
            source_currency: rate_currency,
            target_currency: rate_target_currency,
            bid: message.payload.bid,
            ask: message.payload.ask,
            high: message.payload.high,
            low: message.payload.low,
            close: message.payload.close,
            open: message.payload.open,
            is_open_market: message.payload.is_open_market,
          }),
        )
        dispatch(
          setTickerDataFromRateAsync({
            key: rate_currency + '_' + rate_target_currency,
            rate,
            time: message.payload.time,
            source_currency: rate_currency,
            target_currency: rate_target_currency,
            bid: message.payload.bid,
            ask: message.payload.ask,
            high: message.payload.high,
            low: message.payload.low,
            close: message.payload.close,
            open: message.payload.open,
            is_open_market: message.payload.is_open_market,
          }),
        )

        break
      case Events.GetOrderBook:
        const order_book_source_currency = message.payload.source_currency
        const order_book_target_currency = message.payload.target_currency
        const order_book_market = message.payload.market
        dispatch(
          setOrderBookAsync({
            market: order_book_market,
            source_currency: order_book_source_currency,
            target_currency: order_book_target_currency,
            asks: message.payload.asks,
            bids: message.payload.bids,
          }),
        )
        break
      case Events.GetTrades:
        const trades_history_source_currency = message.payload.source_currency
        const trades_history_target_currency = message.payload.target_currency
        dispatch(
          setTradesHistoryAsync({
            source_currency: trades_history_source_currency,
            target_currency: trades_history_target_currency,
            trades: message.payload.trades,
          }),
        )
        break
      case Events.GetTicker:
        dispatch(
          setTickerDataAsync({
            entities: message.payload.entities,
            period: message.payload.period,
          }),
        )
        break
      case Events.EventGetMarketsByFilter:
        const marketsData = message.payload?.markets
        const marketsArray = Array.isArray(marketsData) ? marketsData : []
        const nextCursor = message.payload?.next_cursor ?? null
        const hasMore = message.payload?.has_more ?? false
        dispatch(
          setMarketDataAsync({
            markets: marketsArray,
            next_cursor: nextCursor,
            has_more: hasMore,
          }),
        )
        break
      case Events.GetMarketsByIds:
        const marketsByIdsData = message.payload?.markets
        const marketsByIdsArray = Array.isArray(marketsByIdsData) ? marketsByIdsData : []
        dispatch(
          setMarketsByIdsDataAsync({
            markets: marketsByIdsArray,
          }),
        )
        break
      case Events.SocketStatusConnection:
        dispatch(
          setPublicStatusAsync({
            socket_status: message.status,
          }),
        )
        break
      case Events.GetMarketInfo:
        dispatch(
          setMarketInfoAsync({
            market_info: message.payload?.market_info,
          }),
        )
        break
    }
  }
  return processMessageFunc
}
export const setPublicStatusAsync = createAsyncThunk('socket/setPublicStatusAsync', async (data: any) => {
  return data
})
export const setRateAsync = createAsyncThunk('socket/setRateAsync', async (data: IActualRate) => {
  return data
})
export const setOrderBookAsync = createAsyncThunk('socket/setOrderBookAsync', async (data: IActualOrderBook) => {
  return data
})
export const setTradesHistoryAsync = createAsyncThunk(
  'socket/setTradesHistoryBookAsync',
  async (data: IActualTradesHistory) => {
    return data
  },
)
export const setTickerDataAsync = createAsyncThunk('socket/setTickerDataAsync', async (data: ITickerData) => {
  return data
})

export const initTickerDataAsync = createAsyncThunk('socket/initTickerDataAsync', async (data: ConfigMarket[]) => {
  return data
})
export const setTickerDataFromRateAsync = createAsyncThunk(
  'socket/setTickerDataFromRateAsync',
  async (data: IActualRate) => {
    return data
  },
)

export const setMarketDataAsync = createAsyncThunk(
  'socket/setMarketDataAsync',
  async (data: { markets: IConfigMarket[], next_cursor?: string | null, has_more?: boolean }) => {
    return data
  },
)

export const setMarketsByIdsDataAsync = createAsyncThunk(
  'socket/setMarketsByIdsDataAsync',
  async (data: { markets: IConfigMarket[] }) => {
    return data
  },
)

export const setMarketInfoAsync = createAsyncThunk(
  'socket/setMarketInfoAsync',
  async (data: { market_info: IMarketInfo }) => {
    return data
  },
)

export const subscribeToPublicService = createAsyncThunk(
  'socket/subscribeToPublicService',
  async ({ event, params = {} }: { event: Events; params?: ISubscribeParams }, { dispatch }) => {
    SocketMiddleware.addSubscriber(params, event, PublicServiceConnection, getProcessMessageFunc(dispatch))
    return null
  },
)

export const sendGetMarketInfo = createAsyncThunk(
  'socket/sendGetMarketInfo',
  async (params: { source_currency: string; target_currency: string }, { dispatch }) => {
    PublicServiceConnection.request(params, Events.GetMarketInfo, getProcessMessageFunc(dispatch))
    return null
  },
)

export const unsubscribeToPublicService = createAsyncThunk(
  'socket/unsubscribeToPublicService',
  async ({ event, params = {} }: { event: string; params?: ISubscribeParams }) => {
    SocketMiddleware.deleteSubscriber(params, event, PublicServiceConnection)
    return { source_currency: params.source_currency, target_currency: params.target_currency }
  },
)
export const requestMarkets = createAsyncThunk(
  'socket/requestMarkets',
  async ({ filter, limit, cursor }: { filter?: string, limit?: number, cursor?: string } = {}) => {
    PublicServiceConnection.requestMarkets(filter, limit, cursor)
    return { isInitialLoad: !cursor }
  },
)

export const requestMarketsByIds = createAsyncThunk(
  'socket/requestMarketsByIds',
  async ({ ids }: { ids: number[] }) => {
    PublicServiceConnection.requestMarketsByIds(ids)
    return { ids }
  },
)

export const PublicServiceConnectionSlice = createSlice({
  name: 'public_service',
  initialState,
  reducers: {
    clearMarkets(state) {
      state.markets = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setMarketDataAsync.fulfilled, (state, action) => {
        const selectTab = localStorageManager.get('selectedMarketTab') || ''
        if (state.last_selected_tab !== selectTab) {
          state.markets = []
          state.last_selected_tab = selectTab
        }

        const newMarkets = action.payload.markets.map((m: any) => new ConfigMarket(m))

        if (state.markets.length === 0) {
          state.markets = newMarkets
        } else {
          const marketsMap = new Map(state.markets.map(m => [m.id, m]))
          newMarkets.forEach(market => {
            marketsMap.set(market.id, market)
          })
          state.markets = Array.from(marketsMap.values())
        }
      })
      .addCase(setMarketsByIdsDataAsync.fulfilled, (state, action) => {
        const selectTab = localStorageManager.get('selectedMarketTab') || ''
        if (state.last_selected_tab !== selectTab) {
          state.markets = []
          state.last_selected_tab = selectTab
        }

        const newMarkets = action.payload.markets.map((m: any) => new ConfigMarket(m))
        state.markets = newMarkets
      })
      .addCase(setRateAsync.fulfilled, (state, action) => {
        if (
          !state.rates[action.payload.key]?.check_time ||
          Date.now() - state.rates[action.payload.key]?.check_time > 200
        ) {
          state.rates[action.payload.key] = {
            rate: action.payload.rate,
            time: action.payload.time,
            source_currency: action.payload.source_currency,
            target_currency: action.payload.target_currency,
            check_time: Date.now(),
          }
        }
      })
      .addCase(unsubscribeToPublicService.fulfilled, (state, action) => {
        delete state.rates[action.payload.source_currency + '_' + action.payload.target_currency]
      })
      .addCase(setOrderBookAsync.fulfilled, (state, action) => {
        const market = getMarket(action.payload.source_currency, action.payload.target_currency)
        state.order_book_by_market[market] = action.payload
      })
      .addCase(setTradesHistoryAsync.fulfilled, (state, action) => {
        const market = getMarket(action.payload.source_currency, action.payload.target_currency)
        const trades = []
        for (const i in action.payload.trades) {
          trades.push(new TradesEntity(action.payload.trades[i]))
        }
        if (state.trades_history_by_market[market]?.trades?.length) {
          if (
            !state.trades_history_by_market[market].time ||
            Date.now() - state.trades_history_by_market[market].time > 200
          ) {
            state.trades_history_by_market[market] = {
              source_currency: action.payload.source_currency,
              target_currency: action.payload.target_currency,
              trades: trades,
              time: Date.now(),
            }
          }
        } else {
          state.trades_history_by_market[market] = {
            source_currency: action.payload.source_currency,
            target_currency: action.payload.target_currency,
            trades: trades,
            time: Date.now(),
          }
        }
      })
      .addCase(setTickerDataAsync.fulfilled, (state, action) => {
        const allTickers = state.rates_ticker.entities
        const tickersUpdate: ITickerEntity = {}

        Array.isArray(action.payload.entities) &&
        action.payload.entities?.map(
          (entity: ITickerEntityData) =>
            (tickersUpdate[entity.source_currency + '_' + entity.target_currency] = entity),
        )

        state.rates_ticker.entities = Object.assign(allTickers, tickersUpdate)
      })
      .addCase(setTickerDataFromRateAsync.fulfilled, (state, action) => {
        state.rates_ticker.entities[action.payload.key] = {
          ...action.payload,
          ask: action.payload.ask ?? action.payload.rate,
          bid: action.payload.bid ?? action.payload.rate,
          close: action.payload.close ?? action.payload.rate,
          open: action.payload.open ?? action.payload.rate,
          low: action.payload.low ?? action.payload.rate,
          high: action.payload.high ?? action.payload.rate,
          volume_source_currency: action.payload.ask ?? action.payload.rate,
          volume_target_currency: action.payload.bid ?? action.payload.rate,
          is_open_market: action.payload.is_open_market ?? action.payload.is_open_market,
        }
      })
      .addCase(initTickerDataAsync.fulfilled, (state, action) => {
        const allTickers = state.rates_ticker.entities
        const tickersUpdate: ITickerEntity = {}
        action.payload.forEach((market) => {
          tickersUpdate[market.source_currency + '_' + market.target_currency] = {
            source_currency: market.source_currency,
            target_currency: market.target_currency,
            ask: 0,
            bid: 0,
            close: 0,
            open: 0,
            low: 0,
            high: 0,
            volume_source_currency: 0,
            volume_target_currency: 0,
            is_open_market: false,
          }
        })
        state.rates_ticker.entities = Object.assign(allTickers, tickersUpdate)
      })
      .addCase(setPublicStatusAsync.fulfilled, (state, action) => {
        state.socket_status = action.payload.socket_status
      })
      .addCase(setMarketInfoAsync.fulfilled, (state, action) => {
        state.market_info = action.payload.market_info
      })
  },
})

export const getRates = (root: RootState) => {
  return root.public_service_connector.rates ?? {}
}

export const getActualRate = createSelector(
  getRates,
  (_: RootState, sourceCurrency: string, targetCurrency: string) => ({
    sourceCurrency,
    targetCurrency,
  }),
  (rates, { sourceCurrency, targetCurrency }) => {
    return rates[sourceCurrency + '_' + targetCurrency]
  },
)

export const getRatesByCurrencies = (root: RootState, currency: string, target_currency: string) => {
  return root.public_service_connector.rates[currency + '_' + target_currency]?.rate
}

export const selectTickerEntities = (root: RootState) => root.public_service_connector.rates_ticker?.entities

export const selectTickerByPair = createSelector(
  selectTickerEntities,
  (_: RootState, source: string, target: string) => `${source}_${target}`,
  (entities, key) => entities?.[key] ?? null,
)

export const getTickerDataAll = (root: RootState): ITickerData => {
  return root.public_service_connector.rates_ticker ?? {}
}
export const getTickerData = (root: RootState, source: string, target: string) => {
  return root.public_service_connector.rates_ticker?.entities[getMarket(source, target, '_')] ?? {}
}

export const getOrderBookByCurrencies = (root: RootState, currency: string, target_currency: string) => {
  return root.public_service_connector.order_book_by_market[getMarket(currency, target_currency)] ?? {}
}

export const getTradesHistoryByCurrencies = (root: RootState, currency: string, target_currency: string) => {
  return root.public_service_connector.trades_history_by_market[getMarket(currency, target_currency)] ?? {}
}

export const getMarkets = (root: RootState): IConfigMarket[] => {
  return root.public_service_connector.markets ?? {}
}

export const getMarketInfo = (root: RootState, source: string, target: string): IMarketInfo|undefined =>
  root.public_service_connector?.market_info?.source_currency === source && root.public_service_connector?.market_info?.target_currency === target ? root.public_service_connector?.market_info : undefined

export const { clearMarkets } = PublicServiceConnectionSlice.actions

export default PublicServiceConnectionSlice.reducer
